import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
  useRef,
} from "react";
import copyClipboard from "../../utils/copyClipboard";
import { getCachedScript } from "../../utils/scriptCache.js";
import { loadLocalModule } from "../../utils/nodeLoader";
import "./ButtonCard.css";
import { hasCategory } from "../../utils/subscription.js";

import closeComps from "../../utils/buttons/closeComps";
import kanyeWest from "../../utils/buttons/kanyeWest";
import openAi from "../../utils/openAi";

const ButtonCard = ({
  currentUser,
  useVisible,
  saveCepStorage,
  getCepStorage,
  useCepStorage,
  button,
  selectedTag,
  tags,
  search,
  ComponentToRender,
  buttonViewMode = "list",
  showGridButtonTooltip = false,
}) => {
  const STORAGE_PATH = `buttons.${button._id}`;
  const ENABLED_PATH = `${STORAGE_PATH}.enabled`;
  const OPTIONS_PATH = `${STORAGE_PATH}.options`;
  const TAGS_PATH = `${STORAGE_PATH}.tags`;

  const [isVisible, setIsVisible] = useCepStorage(ENABLED_PATH);
  const { category, name, script, argument, icon } = button;

  const [buttonTags, setButtonTags] = useState(() => {
    const savedTags = getCepStorage(TAGS_PATH);
    return savedTags || [];
  });

  const buttonTagsSet = useMemo(() => new Set(buttonTags), [buttonTags]);
  const buttonCardRef = useRef(null);
  const [searchMatch, setSearchMatch] = useState(true);
  const normalizedSearch = (search || "").trim().toLowerCase();
  const normalizedButtonViewMode = buttonViewMode === "grid" ? "grid" : "list";

  const toggleButtonTag = useCallback((id, event) => {
    event.preventDefault();
    event.stopPropagation();

    setButtonTags((prevTags) => {
      const newTags = new Set(prevTags);
      if (newTags.has(id)) {
        newTags.delete(id);
      } else {
        newTags.add(id);
      }
      return Array.from(newTags);
    });
  }, []);

  useEffect(() => {
    saveCepStorage(TAGS_PATH, buttonTags);
  }, [buttonTags, TAGS_PATH, saveCepStorage]);

  useEffect(() => {
    if (!normalizedSearch) {
      setSearchMatch(true);
      return;
    }

    const searchableText = buttonCardRef.current?.textContent?.toLowerCase() || "";
    const hasMatch = searchableText.includes(normalizedSearch);
    setSearchMatch(hasMatch);
  }, [normalizedSearch]);

  function handleClick() {
    if (script !== "") {
      const code = getCachedScript();
      if (code) {
        window.jsx.evalScript(
          `run(${script}(${JSON.stringify(argument)}));`,
          function (result) {
            if (script === "controlsDR" && result) {
              copyClipboard(result);
            }
          },
        );
      }
    }

    if (name.toLowerCase().match("close compositions")) {
      closeComps();
    }

    if (name.toLowerCase().match("kanye west")) {
      kanyeWest();
    }

    if (name.toLowerCase().match("test")) {
      const fontModule = loadLocalModule("fontLogic");
      if (fontModule) {
        fontModule.getSystemFonts((res) => {
          if (res.error) {
            alert("Ошибка: " + res.error);
          } else if (res.fonts && res.fonts.length > 0) {
            // Выводим первый шрифт и общее количество
            alert(
              `Найдено ${res.fonts.length} шрифтов.\nПервый: ${res.fonts[0]}`,
            );
            console.log("Все шрифты:", res.fonts);
          } else {
            alert("Шрифты не найдены в системной папке.");
          }
        });
      }
    }
  }

  // Возвращаем функцию generateButtonTags для совместимости
  const generateButtonTags = useCallback(() => {
    return (
      <div className="button-card__tags" onClick={(e) => e.stopPropagation()}>
        {tags.map((tag) => (
          <div
            key={tag.id}
            className={`button-tag tags-container__tag ${
              buttonTagsSet.has(tag.id) ? "active" : ""
            }`}
            onClick={(event) => toggleButtonTag(tag.id, event)}
            style={{
              backgroundColor: tag.color,
              cursor: "pointer",
              position: "relative",
              zIndex: 2,
            }}
            title={tag.name}
          />
        ))}
      </div>
    );
  }, [tags, buttonTagsSet, toggleButtonTag]);

  if (ComponentToRender) {
    if (selectedTag && !buttonTagsSet.has(selectedTag)) {
      return null;
    }

    if (normalizedSearch && !searchMatch) {
      return null;
    }

    return (
      <li
        className={`button-group button-group--${normalizedButtonViewMode}`}
        ref={buttonCardRef}
        title={showGridButtonTooltip ? name : undefined}
      >
        <ComponentToRender
          useVisible={useVisible}
          saveCepStorage={saveCepStorage}
          getCepStorage={getCepStorage}
          useCepStorage={useCepStorage}
          isVisible={isVisible}
          setIsVisible={setIsVisible}
          optionsPath={OPTIONS_PATH}
          selectedTag={selectedTag}
          buttonTags={buttonTags}
          generateButtonTags={generateButtonTags} // Передаем с оригинальным названием
          category={category}
          button={button}
          buttonViewMode={normalizedButtonViewMode}
        />
      </li>
    );
  }

  const isButtonDisabled =
    currentUser.subscription && !hasCategory(currentUser.subscription, category);

  if (normalizedSearch && !searchMatch) {
    return null;
  }

  if (selectedTag && !buttonTagsSet.has(selectedTag)) {
    return null;
  }

  return (
    <li
      ref={buttonCardRef}
      className={`button-card button-card--${normalizedButtonViewMode} ${
        isButtonDisabled ? "hidden" : ""
      }`}
      onClick={isButtonDisabled ? null : handleClick}
      title={showGridButtonTooltip ? name : undefined}
    >
      <div className="button-card__icon">
        <div dangerouslySetInnerHTML={{ __html: icon }} />
      </div>
      <div className="button-card__title">{name}</div>
      {generateButtonTags()}
    </li>
  );
};

export default React.memo(ButtonCard);
